import React, { useState, useMemo } from "react";
import { ChatSender } from "../../common/constants";
import { mapIconUrlToGCP } from "../../common/gcp";
import { getUserConfigText, DEFAULT_TEXT } from "../../config";
import { calcContrastColor } from "../Widget/BubbleWidget";
import { useConfig } from "../Widget/ConfigContext";
import { useHistoryContext } from "./ChatHistory";

export function BubbleWrapper({
  children,
  minimal,
  sender,
  copyMessage,
  speakMessage,
  stopMessage,
  showTts,
}) {
  const { config, colors } = useConfig();
  const { synthKey } = useHistoryContext();
  const [showCopyButton, setShowCopyButton] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const copyMessageText = useMemo(
    () =>
      getUserConfigText(
        config,
        "copyMessageText",
        DEFAULT_TEXT.en.copyMessageText,
      ),
    [document.cookie, config.overrideLanguage],
  );

  const bubbleColors = useMemo(
    () => ({
      bot: colors.botMessageBubble,
      human: colors.userMessageBubble,
      human_agent: colors.humanAgentMessageBubble,
    }),
    [colors],
  );

  const textColors = useMemo(
    () => ({
      bot: colors.botMessageText,
      human: colors.userMessageText,
      human_agent: colors.humanAgentMessageText,
    }),
    [colors],
  );

  function handleMouseEnter() {
    if (config.showCopyButtons) {
      setShowCopyButton(true);
    }
  }

  function handleMouseLeave() {
    if (config.showCopyButtons) {
      setShowCopyButton(false);
      setHasCopied(false);
    }
  }

  function handleCopyClick() {
    setHasCopied(true);
    copyMessage();
  }

  function handleSpeakClick() {
    setIsSpeaking(true);
    speakMessage(() => {
      setIsSpeaking(false);
    });
  }

  function handleStopSpeakClick() {
    speechSynthesis.cancel();
    setIsSpeaking(false);
  }

  return (
    <>
      {!minimal ? (
        <>
          <div
            dir="auto"
            style={{
              // magicform-message-bubble
              margin: "0.25rem 0.25rem",
              padding: "1rem 1rem",
              borderRadius: "8px",
              boxSizing: "border-box",
              maxWidth: "80%",
              overflowAnchor: "none",
              lineHeight: "1.5",
              // align right for human, left for all other
              alignSelf:
                sender === ChatSender.human ? "flex-end " : "flex-start",
              backgroundColor: bubbleColors[sender],
              position: "relative",
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              style={
                showCopyButton
                  ? {
                      position: "absolute",
                      display: "block",
                      top: "-10px",
                      padding: "0px",
                      margin: "0px",
                      backgroundColor:
                        sender === ChatSender.bot
                          ? colors.botMessageBubble
                          : colors.userMessageBubble,
                      filter: "brightness(120%)",
                      boxShadow: "0 5px 10px rgb(0 0 0 / 0.25)",
                      border: "none",
                      borderRadius: "200px",
                      width: "25px",
                      height: "25px",
                      ...(sender === ChatSender.human
                        ? {
                            left: "-10px",
                          }
                        : { right: "-10px" }),
                    }
                  : { display: "none" }
              }
              onClick={() => handleCopyClick()}
              title={copyMessageText}
            >
              {hasCopied ? (
                <img
                  src={mapIconUrlToGCP(config.checkIconUrl)}
                  alt="Check icon"
                  className="check-fade-in"
                  style={{
                    margin: "0",
                    padding: "5px",
                    width: "100%",
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "100%",
                    filter:
                      calcContrastColor(colors.botMessageBubble) === "white"
                        ? "invert(100%)"
                        : "",
                  }}
                />
              ) : (
                <img
                  src={mapIconUrlToGCP(config.copyIconUrl)}
                  alt="clipboard icon"
                  style={{
                    margin: "0",
                    padding: "5px",
                    width: "100%",
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "100%",
                    filter:
                      calcContrastColor(colors.botMessageBubble) === "white"
                        ? "invert(100%)"
                        : "",
                  }}
                />
              )}
            </button>
            <div
              style={{
                fontSize: config.messageFontSize,
                color: textColors[sender],
                fontFamily: "sans-serif",
                margin: "0",
                overflowWrap: "break-word",
                position: "static",
              }}
            >
              {children}
            </div>
            {showTts !== false &&
              synthKey &&
              config?.showTtsButtons &&
              sender !== ChatSender.human && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-start",
                    marginBottom: "-0.5rem",
                    marginLeft: "-0.5rem",
                  }}
                >
                  {isSpeaking ? (
                    <>
                      <button
                        onClick={() => handleStopSpeakClick()}
                        aria-label="stop currently playing message"
                        className="magicform-speaker-button"
                        style={{
                          height: "28px",
                          width: "28px",
                          marginTop: "5px",
                          border: "none",
                          borderRadius: "2rem",
                        }}
                      >
                        <img
                          src={mapIconUrlToGCP(config.stopIconUrl)}
                          alt="stop symbol"
                          style={{
                            width: "15px",
                            height: "15px",
                            filter:
                              calcContrastColor(colors.botMessageBubble) ===
                              "white"
                                ? "invert(100%)"
                                : "",
                          }}
                        />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleSpeakClick()}
                      className="magicform-speaker-button"
                      aria-label="play message via audio transcription"
                      style={{
                        height: "28px",
                        width: "28px",
                        marginTop: "5px",
                        border: "none",
                        borderRadius: "2rem",
                      }}
                    >
                      <img
                        src={mapIconUrlToGCP(config.speakerIconUrl)}
                        alt="speaker icon"
                        style={{
                          width: "15px",
                          height: "15px",
                          filter:
                            calcContrastColor(colors.botMessageBubble) ===
                            "white"
                              ? "invert(100%)"
                              : "",
                        }}
                      />
                    </button>
                  )}
                </div>
              )}
          </div>
        </>
      ) : (
        <div
          // sets which side of the chat window the bubble should align to,
          // human on right, bot or any other on left
          dir="auto"
          style={{
            margin: "0.25rem 0.25rem",
            padding: "1rem 1rem",
            borderRadius: "8px",
            borderBottomRightRadius: "0",
            border: "1px solid lightgray",
            boxSizing: "border-box",
            maxWidth: "100%",
            overflowAnchor: "none",
            lineHeight: "1.5",
            cursor: "pointer",
            backgroundColor: bubbleColors[sender],
            alignSelf: "flex-end", // when minimal, should align to right = flex-end
          }}
        >
          <div
            style={{
              color: textColors[sender],
            }}
          >
            {children}
          </div>
        </div>
      )}
    </>
  );
}
